/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import { atom, useAtom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { useEffect } from 'react';

import { useLazyRef } from '@gitbook/util-react-hooks';

export enum InteractionLocks {
    KEYBOARD_ESCAPE = 'KEYBOARD_ESCAPE',
    KEYBOARD_TAB = 'KEYBOARD_TAB',
    OUTSIDE_CLICK = 'OUTSIDE_CLICK',
}

let ID = 0;

const locksAtom = atom<Record<InteractionLocks, string[]>>({
    [InteractionLocks.KEYBOARD_ESCAPE]: [],
    [InteractionLocks.KEYBOARD_TAB]: [],
    [InteractionLocks.OUTSIDE_CLICK]: [],
});

const lockAtom = atomFamily((lock: InteractionLocks) =>
    atom(
        (get) => get(locksAtom)[lock],
        (get, set, update: string[] | ((previous: string[]) => string[])) => {
            const previous = get(locksAtom)[lock];
            const next = typeof update === 'function' ? update(previous) : update;
            set(locksAtom, (current) => ({
                ...current,
                [lock]: next,
            }));
        }
    )
);

/**
 * Returns whether the component calling the hook is the first responder for a specific interaction
 * (for example "responding to ESCAPE").
 *
 * Several components might listen to a specific interaction but only one should
 * respond to an individual event (to avoid closing all the modals, popover
 * and sidesheets as once for example). Only the top-most should.
 * That component is called the "first responder".
 *
 * So when implementing a component that listen to a specific interaction, you should
 * check if it's the first responder:
 *
 * ```js
 * function MyModal({ opened, onClose }) {
 *     const isFirstResponder = useFirstResponderInteraction(InteractionLocks.OUTSIDE_CLICK, opened);
 *
 *     if (!opened) { return null; }
 *     return (
 *         // backdrop
 *         <button onClick={() => {
 *             if (isFirstResponder) { onClose(); }
 *         }}>
 *             <ModalBody>...</ModalBody>
 *         </button>
 *     );
 * }
 * ```
 */
export function useFirstResponderInteraction(
    lockType: InteractionLocks,
    isResponder = true,
    deps: any[] = []
): boolean {
    const idRef = useLazyRef(() => {
        ID += 1;
        return `${ID}`;
    });

    const [lock, setLock] = useAtom(lockAtom(lockType));

    useEffect(() => {
        if (!isResponder) {
            return;
        }

        setLock((previous) => [idRef.current, ...previous]);

        return () => {
            setLock((previous) => previous.filter((id) => id !== idRef.current));
        };
    }, [...deps, isResponder, idRef, lockType, setLock]);

    return lock[0] === idRef.current;
}
